import { useState, useEffect } from 'react';
import { X, Boxes, Archive, ArchiveRestore, ChevronRight } from 'lucide-react';
import { useToast } from '../../components/boothly/toast-provider.jsx';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';
import { SettingsSegmentedRow } from '../../components/boothly/settings-ui.jsx';
import { fmtCurrency } from '../../lib/format.js';

// ── ProductSheet ───────────────────────────────────────────────────────────────
// Anlegen / Bearbeiten eines Katalog-Produkts. Preis lebt hier (Haupteinstellung).
// cost_mode: 'flat' → Kostenfeld sichtbar; 'batch' → Kostenfeld weg + Lager-Verweis.
// Pro-Kanal-Preis = optionaler, demoteter Override via product_channel_prices.
// Rendert über der CatalogSheet (z-[60]).

const inputCls =
  'w-full rounded-xl bg-secondary/50 px-4 py-3 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ios-blue/50';

export function ProductSheet({ open, onOpenChange, store, currentRole, dark, product, onSaved }) {
  const toast = useToast();
  const confirm = useConfirm();
  const isEdit = !!product;
  const archived = !!product?.archivedAt;
  const canManage = ['owner', 'admin'].includes(currentRole);

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [costMode, setCostMode] = useState('flat');
  const [costFlat, setCostFlat] = useState('');
  // Override-Map: { [channelId]: priceString } — leer = kein Override
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  // Form beim Öffnen seeden
  useEffect(() => {
    if (!open) return;
    setName(product?.name ?? '');
    setPrice(product?.price != null ? String(product.price) : '');
    setCostMode(product?.costMode === 'batch' ? 'batch' : 'flat');
    setCostFlat(product?.costFlat != null ? String(product.costFlat) : '');
    setOverrides({});
    // Bestehende Overrides nur im Edit-Modus laden (Produkt muss existieren)
    if (product?.id && store.apiGetProductChannelPrices) {
      store.apiGetProductChannelPrices(product.id).then(rows => {
        const map = {};
        (rows || []).forEach(r => { map[r.channelId] = String(r.price); });
        setOverrides(map);
      });
    }
  }, [open, product]); // eslint-disable-line react-hooks/exhaustive-deps

  const priceNum = parseFloat(price);
  const nameOk = name.trim().length > 0;
  const priceOk = Number.isFinite(priceNum) && priceNum >= 0;
  const canSave = canManage && nameOk && priceOk && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        price: priceNum,
        cost_mode: costMode,
        cost_flat: costMode === 'flat' ? (parseFloat(costFlat) || 0) : null,
      };
      let saved;
      if (isEdit) {
        saved = await store.apiProductUpdate(product.id, payload);
        if (!saved) return;
        store.setProducts(prev => prev.map(p => (p.id === product.id ? saved : p)));
        // Pro-Kanal-Overrides als VOLL-Set speichern (REPLACE-Semantik)
        const prices = (store.channels || [])
          .map(c => ({ channel_id: c.id, raw: overrides[c.id] }))
          .filter(o => o.raw != null && String(o.raw).trim() !== '' && Number.isFinite(parseFloat(o.raw)))
          .map(o => ({ channel_id: o.channel_id, price: parseFloat(o.raw) }));
        await store.apiSetProductChannelPrices(product.id, prices);
      } else {
        saved = await store.apiProductCreate(payload);
        if (!saved) return;
        store.setProducts(prev => [...prev, saved]);
      }
      toast.success(isEdit ? 'Produkt gespeichert' : 'Produkt angelegt');
      onSaved?.(saved);
      onOpenChange(false);
    } catch {
      toast.error('Produkt konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!product?.id) return;
    if (!archived) {
      const ok = await confirm({
        title: 'Produkt archivieren?',
        subtitle: `„${product.name}" verschwindet aus Katalog und Produktauswahl. Bestehende Bestellungen bleiben erhalten.`,
        confirmLabel: 'Archivieren',
        destructive: true,
      });
      if (!ok) return;
    }
    const updated = await store.apiProductArchive(product.id, !archived);
    if (updated) {
      store.setProducts(prev => prev.map(p => (p.id === product.id ? updated : p)));
      toast.success(archived ? 'Produkt wiederhergestellt' : 'Produkt archiviert');
      onOpenChange(false);
    }
  };

  const openLagerForProduct = () => {
    if (!product?.id) return;
    store.setLagerProductId(product.id);
    onOpenChange(false);
    store.setShowCatalog(false);
    store.setShowLager(true);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop (über der CatalogSheet) */}
      <div
        className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[20px]"
        style={{ animation: 'fadeIn 0.2s ease' }}
        onClick={() => onOpenChange(false)}
      />

      {/* Sheet */}
      <div
        data-testid="sheet-product"
        className="fixed inset-x-0 bottom-0 z-[60] flex max-h-[92vh] flex-col rounded-t-3xl"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          animation: 'slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          background: dark ? '#2C2C2E' : '#FFFFFF',
        }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="bottom-sheet-handle" />
        </div>

        <div className="flex items-center justify-between px-5 pb-4">
          <h2 className="text-xl font-semibold">{isEdit ? 'Produkt bearbeiten' : 'Neues Produkt'}</h2>
          <button onClick={() => onOpenChange(false)} className="ghost-icon-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-5">
          {/* Name */}
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Name</label>
            <input
              type="text"
              data-testid="product-name-input"
              placeholder="Produktname"
              value={name}
              onChange={e => setName(e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Preis */}
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Verkaufspreis</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
              <input
                type="number"
                inputMode="decimal"
                data-testid="product-price-input"
                placeholder="0.00"
                value={price}
                onChange={e => setPrice(e.target.value)}
                className={`${inputCls} pl-8`}
              />
            </div>
          </div>

          {/* Kosten-Modus */}
          <div className="glass-card glass-card-online rounded-2xl overflow-hidden">
            <SettingsSegmentedRow
              label="Kosten"
              sub={costMode === 'flat' ? 'Fester Wert pro Stück' : 'FIFO aus dem Lager des Produkts'}
              options={[{ v: 'flat', l: 'Fester Wert' }, { v: 'batch', l: 'Aus Lager' }]}
              value={costMode}
              onChange={setCostMode}
            />
          </div>

          {/* Kostenfeld (nur flat) ODER Lager-Verweis (nur batch) */}
          {costMode === 'flat' ? (
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Kosten pro Stück</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                <input
                  type="number"
                  inputMode="decimal"
                  data-testid="product-costflat-input"
                  placeholder="0.00"
                  value={costFlat}
                  onChange={e => setCostFlat(e.target.value)}
                  className={`${inputCls} pl-8`}
                />
              </div>
            </div>
          ) : isEdit ? (
            <button
              onClick={openLagerForProduct}
              data-testid="product-manage-lager"
              className="w-full flex items-center gap-3 glass-card glass-card-online rounded-2xl px-4 py-3 text-left active:opacity-80 transition-opacity"
            >
              <Boxes className="h-5 w-5 text-ios-blue flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[15px]">Lager verwalten</div>
                <div className="text-[12px] text-[var(--label-tertiary)] mt-0.5">Chargen + FIFO-Bestand dieses Produkts</div>
              </div>
              <ChevronRight size={16} className="text-[var(--label-tertiary)] flex-shrink-0" />
            </button>
          ) : (
            <div className="rounded-xl bg-ios-blue/10 p-4 text-sm text-muted-foreground">
              Produkt zuerst speichern, dann lässt sich das Lager (Chargen) anlegen.
            </div>
          )}

          {/* Optionaler Pro-Kanal-Preis (nur Edit, demoted) */}
          {isEdit && (store.channels || []).length > 0 && (
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Optionaler Pro-Kanal-Preis
              </label>
              <div className="glass-card glass-card-online rounded-2xl p-4 space-y-3">
                <p className="text-[12px] text-[var(--label-tertiary)]">
                  Leer lassen = es gilt der Verkaufspreis oben. Ein Wert überschreibt ihn nur für diesen Kanal.
                </p>
                {(store.channels || []).map(c => (
                  <div key={c.id} className="flex items-center gap-3">
                    <span className="flex-1 text-sm truncate">{c.name}</span>
                    <div className="relative w-28 flex-shrink-0">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        placeholder={fmtCurrency(priceOk ? priceNum : 0)}
                        value={overrides[c.id] ?? ''}
                        onChange={e => setOverrides(prev => ({ ...prev, [c.id]: e.target.value }))}
                        className="w-full rounded-lg bg-secondary/50 pl-7 pr-2 py-2 text-sm outline-none focus:ring-2 focus:ring-ios-blue/50"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Speichern */}
          <button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="product-save-btn"
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-ios-blue py-3 font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-40"
          >
            {isEdit ? 'Speichern' : 'Produkt anlegen'}
          </button>

          {/* Archivieren / Wiederherstellen (nur Edit) */}
          {isEdit && canManage && (
            <button
              onClick={handleArchiveToggle}
              data-testid="product-archive-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-secondary/50 py-3 text-sm font-semibold active:scale-[0.98] transition-all"
              style={{ color: archived ? 'var(--accent-online)' : 'var(--accent-orange-strong)' }}
            >
              {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              {archived ? 'Wiederherstellen' : 'Produkt archivieren'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
